import type { TFunction } from 'i18next';
import { PostWorkflowBatchResult } from 'app/api/postTimeSavers';

type Reason = { key: string; defaultValue: string };

const GENERIC_REASON: Reason = {
  key: 'assignTasksResult.failureReason.generic',
  defaultValue: 'Some posts could not be assigned.',
};

const REASON_BY_ERROR_CODE: Record<string, Reason> = {
  invalid_source_status: {
    key: 'assignTasksResult.failureReason.invalidStatus',
    defaultValue:
      "Some posts couldn't be assigned because a task can't be added to them in their current status.",
  },
  access_denied: {
    key: 'assignTasksResult.failureReason.accessDenied',
    defaultValue:
      "Some posts couldn't be assigned because you don't have access to them.",
  },
  post_not_found: {
    key: 'assignTasksResult.failureReason.notFound',
    defaultValue:
      "Some posts couldn't be assigned because they no longer exist.",
  },
};

function isBatchResult(value: unknown): value is PostWorkflowBatchResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as PostWorkflowBatchResult).failed)
  );
}

/**
 * Builds the user-facing summary for a partially/fully failed bulk task assignment.
 * Returns null when the result is not a batch result or has no failures.
 */
export function buildAssignTasksFailureMessage(
  result: unknown,
  t: TFunction,
): string | null {
  if (!isBatchResult(result) || result.failed.length === 0) {
    return null;
  }

  const total = result.succeeded.length + result.failed.length;

  const summary = t('assignTasksResult.summary', {
    defaultValue: 'Task assigned to {{succeeded}} of {{total}} posts.',
    succeeded: result.succeeded.length,
    total,
  });

  const seenKeys = new Set<string>();
  const reasons = result.failed
    .map(
      (failure) =>
        REASON_BY_ERROR_CODE[failure.error_code ?? ''] ?? GENERIC_REASON,
    )
    .filter((reason) => {
      if (seenKeys.has(reason.key)) {
        return false;
      }
      seenKeys.add(reason.key);
      return true;
    })
    .map((reason) => t(reason.key, { defaultValue: reason.defaultValue }))
    .join(' ');

  return `${summary} ${reasons}`.trim();
}
